import { useProposalTypeData } from "../proposal/use-proposal-type-data";
import { ratesFormat18To65GCLI } from "../../utils/rates-format-18-65-gcli.utils";
import { ratesFormat18To65GYRTAndGPA } from "../../utils/rates-format-18-65-gyrt-gpa.utils";
import { generateFormatRatesGCLI, ratesFormat66To80GCLI } from "../../utils/rates-format-66-to-80-gcli.utils";
import { ratesFormat66To80GYRTAndGPA } from "../../utils/rates-format-66-to-80-gyrt-gpa.utils";
import { RATES_CONSTANTS } from "../../constant/gms-constant";
import { useProposalComputation } from "../proposal/use-proposal-computation";

export function useComputeRates() {

    const { 
        isProductGCLI, 
        isRatesIsAgeType,
        basicPlan,
        noOfLives,
        minAgeProfile,
        maxAgeProfile,
        borrowerAge6670: isSelected6670,
        borrowerAge7175: isSelected7175,
        borrowerAge7680: isSelected7680,
    } = useProposalTypeData();
    const {
        rates1865: ratesOf1865,
        rates6670: gcliRates6670,
        rates7175: gcliRates7175,
        rates7680: gcliRates7680,
    } = useProposalComputation()

    const isHighLives = noOfLives >= RATES_CONSTANTS.maxNumberOfLive;

    if (!minAgeProfile || !maxAgeProfile) {
        return {
            formattedFinalAllRates: {},
            isHighLives: false,
            computedFinalRates1865: null,
            final18to65: null,
        };
    }

    const ratesOf1865Safe = ratesOf1865 ?? [];
    const gcliRates6670Safe = gcliRates6670 ?? [];
    const gcliRates7175Safe = gcliRates7175 ?? [];
    const gcliRates7680Safe = gcliRates7680 ?? [];

    let computedFinalRates1865 = null;
    let computedFinalRates6670 = null;
    let computedFinalRates7175 = null;
    let computedFinalRates7680 = null;

    const AGE_1865 = "18-65";
    const AGE_6670 = "66-70";
    const AGE_7175 = "71-75";
    const AGE_7680 = "76-80";

    if (isProductGCLI) {
        const hasMissingRates = !gcliRates6670Safe?.length || !gcliRates7175Safe?.length || !gcliRates7680Safe?.length;
        if (!hasMissingRates) {
            computedFinalRates1865 = ratesFormat18To65GCLI(ratesOf1865Safe);
            computedFinalRates6670 = ratesFormat66To80GCLI(gcliRates6670Safe);
            computedFinalRates7175 = ratesFormat66To80GCLI(gcliRates7175Safe);
            computedFinalRates7680 = ratesFormat66To80GCLI(gcliRates7680Safe);
        }
    } else if (isRatesIsAgeType) {
        computedFinalRates1865 = ratesFormat18To65GYRTAndGPA(ratesOf1865Safe);
        computedFinalRates6670 = ratesFormat66To80GYRTAndGPA(gcliRates6670Safe, AGE_6670);
        computedFinalRates7175 = ratesFormat66To80GYRTAndGPA(gcliRates7175Safe, AGE_7175);
        computedFinalRates7680 = ratesFormat66To80GYRTAndGPA(gcliRates7680Safe, AGE_7680);
    }

    // const transform18to65 = (data: any) => {
    //     if (!data) return null;

    //     const { basic_plan_id, ...rest } = data;

    //     const rates = Object.entries(rest)
    //         .filter(([key]) => key.startsWith("age_"))
    //         .flatMap(([, value]: any) => value);

    //     return {
    //         basic_plan_id,
    //         rates,
    //     };
    // };

    const transform18to65 = ( data: any, minAge: number, maxAge: number ) => {
        if (!data) return {};

        const { basic_plan_id, ...rest } = data;

        const ageEntries = Object.entries(rest).filter(([key]) =>
            key.startsWith("age_")
        );

        return ageEntries.reduce((acc, [key, value], index) => {
            const label =
                index === 0
                    ? `${minAge}-${maxAge}`
                    : `${maxAge + 1}-65`;

            acc[label] = {
                basic_plan_id,
                rates: value,
            };

            return acc;
        }, {} as Record<string, any>);
    };

    const getAgeRate = <T, K extends keyof T>(data: T | null | undefined, key: K) => {
        return data?.[key];
    };
    
    let final66to70
    let final71to75
    let final76to80

    if(isProductGCLI) {
        if(!basicPlan?.id) return
        final66to70 = generateFormatRatesGCLI(gcliRates6670Safe, basicPlan?.id)
        final71to75 = generateFormatRatesGCLI(gcliRates7175Safe, basicPlan?.id)
        final76to80 = generateFormatRatesGCLI(gcliRates7680Safe, basicPlan?.id)
    } else if (isRatesIsAgeType) {
        final66to70 = getAgeRate(computedFinalRates6670, AGE_6670);
        final71to75 = getAgeRate(computedFinalRates7175, AGE_7175);
        final76to80 = getAgeRate(computedFinalRates7680, AGE_7680);
    }
    
    // let final18to65 = computedFinalRates1865?.[AGE_1865];
    // if (isRatesIsAgeType && isHighLives) {
    //     // if this is true, the label will be different,
    //     // minAge = 30, maxAge = 55;
    //     // expected label will be 30-55 and 55-65 instead of 18-65 only
    //     final18to65 = transform18to65(final18to65);
    // }

    const transformed18to65 =
        isRatesIsAgeType && isHighLives
            ? transform18to65(computedFinalRates1865?.[AGE_1865], minAgeProfile, maxAgeProfile)
            : { [AGE_1865]: computedFinalRates1865?.[AGE_1865] };

            const formattedFinalAllRates = {
        ...transformed18to65,

        ...(isSelected6670 && {
            [AGE_6670]: final66to70,
        }),

        ...(isSelected7175 && {
            [AGE_7175]: final71to75,
        }),

        ...(isSelected7680 && {
            [AGE_7680]: final76to80,
        }),
    };

    // const formattedFinalAllRates = {
    //     [AGE_1865]: final18to65,
    //         ...(isSelected6670 && {
    //             [AGE_6670]: final66to70,
    //         }),
    //         ...(isSelected7175 && {
    //             [AGE_7175]: final71to75,
    //         }),
    //         ...(isSelected7680 && {
    //             [AGE_7680]: final76to80,
    //         }),
    // };

    return {
        formattedFinalAllRates,
        isHighLives,
        computedFinalRates1865,
        // final18to65
    };
}